/**
 * Traditional (Anexo C, broker-mediated) base variant.
 *
 * Used for `DealType.Traditional` listings — off-platform spaces
 * facilitated by a SpotMe specialist via WhatsApp.
 *
 * 2026-05-26 course-correction (docs/plans/2026-05-26-001): the primary
 * CTA is now a WhatsApp pill that routes directly to the SpotMe corporate
 * WhatsApp via `buildWhatsappUrl(message)` — no LeadForm in front. The
 * 2 weeks of production PostHog data following the previous overhaul
 * showed the inline LeadForm converted at 0.6-0.8% engagement→submit
 * while the WhatsApp pill converted at ~75% click→qualified-lead. The
 * LeadForm config below is still consumed by the email-secondary path
 * (desktop outlined "Solicitar por correo" + mobile inline body block
 * in `LeadFormInlineCta`) — it just isn't the primary surface anymore.
 */

import type { BaseListingVariant } from 'src/lib/listingVariants/types'

export const baseTraditionalVariant: BaseListingVariant = {
  priceCard: {
    primaryCta: {
      labelKey: 'CTA.WHATSAPP_TRADITIONAL',
      action: 'open-whatsapp'
    },
    secondaryCta: null,
    showScarcity: true,
    scarcityCopyKey: 'SCARCITY.MONTHLY_LAST',
    showWatching: true,
    showStorageTimeChips: false,
    showQuantityInput: false,
    showFleetCta: true,
    showPriceBreakdown: false,
    trustSignals: [
      'traditional.no_commitment',
      'traditional.free_advisor',
      'traditional.verified_spaces'
    ]
  },
  leadForm: {
    // 2026-05-26: the variant's primary CTA is no longer the LeadForm
    // (PriceCard renders the WhatsApp pill directly). This config is
    // still consumed by the secondary email path (desktop outlined
    // button + mobile inline body block) which opens the same LeadForm
    // in a modal drawer. `enabled: true` keeps the drawer functional —
    // PriceCard's inline-on-card branch is gated by
    // `priceCard.primaryCta.action === 'open-whatsapp'` instead.
    enabled: true,
    desktopContainer: 'inline-on-card',
    primaryChannel: 'whatsapp',
    // Step order INVERTED 2026-05-18: contact first, qualifier (now
    // role-shifted to OPTIONAL enrichment) second, success last. The
    // qualifier-first order was filtering qualified intent and tanking
    // submit rates to ~0-2%. The step NAME stays 'qualifier' so existing
    // routing by name keeps working — its semantic role is now "optional
    // enrichment". See spotme-client/src/lib/listingVariants/variants/
    // broker.ts for the broader rationale.
    steps: ['contact', 'qualifier', 'success'],
    fields: [
      { name: 'name', required: true },
      { name: 'phone', required: true },
      // Email is REQUIRED to raise lead quality (standardized across
      // Broker + Traditional + Fleet — the optional-with-upsell variant
      // captured low-value addresses).
      { name: 'email', required: true }
    ],
    // Traditional captures storage time (`when`) as the qualifier rather
    // than sqm: the listing already knows its physical size, but knowing
    // how long the renter plans to store is what lets the specialist
    // shortlist matching off-platform inventory in the first reply.
    qualifierField: 'when',
    // Traditional listings are B2C self-serve: the renter fills the form,
    // submits via WhatsApp, and the host handles the rest off-platform.
    // There's no "scope conversation" with a specialist that would make a
    // dedicated Fernando card useful here, and surfacing one made the
    // page read as more enterprise / friction-heavy than it actually is.
    // Fernando stays on Broker + Fleet (true B2B) and on B2B demand
    // pillar pages — that's where a specialist persona earns its place.
    specialistKey: null,
    responseBadge: { show: true },
    whatsappRecipient: 'specialist',
    method: 'traditional_contact',
    leadType: 'demand',
    subType: 'traditional_contact',
    postSubmit: 'redirect-wa',
    // Off-platform lead: no SpotMe asesor promise, no response-time promise
    // in SpotMe's voice — we neither manage nor observe the party who ends up
    // handling it (spec §3). Governs Step 2's header subtitle as well as Step
    // 3's success copy; both must stay free of a deadline. ⚠️ This picks the
    // copy SET, not the party: who actually contacts the renter is decided
    // server-side by the host-lead DISCLOSURE tier (a Traditional listing on a
    // non-partner host is a PII-free nudge, worked by a SpotMe asesor), so the
    // `traditional` set is deliberately worded to be true on both tiers. See
    // STEP3_COPY in `components/LeadForm/steps/Step3Success.tsx` and
    // STEP2_HEADER_SUBTITLE in `.../Step2Contact.tsx` before editing it.
    leadCopyKey: 'traditional',
    // Renter IS reserving — SpotMe manages the booking off-platform, only
    // the digitization is pending. Broker / Fleet stay neutral.
    useBookingFraming: true
  },
  sections: {
    // Hidden — see specialistKey comment above. The landlord identity
    // stays server-side only ("never reveal the landlord team"), and we
    // no longer show a specialist on Traditional listings either, so the
    // user section has nothing renter-safe to render.
    user: { variant: 'hidden' },
    size: { show: true, framing: 'b2b' },
    insurance: { show: false, mode: 'hidden' },
    reviews: { show: true },
    refundBadge: { show: false },
    similarListings: { show: true, tier: 'off-platform' },
    securityAndSupport: {
      headlineKey: 'SECURITY.TRADITIONAL.TITLE_PREFIX',
      accentKey: 'SECURITY.TRADITIONAL.TITLE_ACCENT',
      subtitleKey: 'SECURITY.TRADITIONAL.SUBTITLE',
      cardKeys: [
        'verification_traditional',
        'anti_fraud',
        'host_direct_traditional',
        'support_specialist_traditional'
      ]
    }
  },
  checkout: {
    authGate: 'none',
    guestEmailOk: false
  }
}
